import { colors } from '@kmns-internal/furikake/tokens'
import {
  Add,
  ChevronRight,
  DeleteOutlined,
  ExpandMore,
  LocationOn,
  MoreHoriz,
} from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { PlaceNodeTypeEnum } from '@ulysses-inc/harami_api_client'
import type React from 'react'
import { type MouseEvent, useState } from 'react'
import PlaceGroupSVG from 'src/assets/icons/place_groups.svg?react'
import Loading from 'src/components/loading/Loading'
import type { PlaceGroupsProps } from 'src/features/places/PlaceGroups'
import type { FlatNode } from 'src/features/places/PlaceGroupsProps'
import { semanticColors } from '../theme/semanticColors'

interface RowData {
  id: number
  uuid: string
  name?: string
}
const PlaceGroupList: React.FC<PlaceGroupsProps> = (
  props: PlaceGroupsProps,
) => {
  const [collapsedGroup, setCollapsedGroup] = useState<number[]>([])

  // 3点リーダーメニュー
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedRow, setSelectedRow] = useState<RowData | null>(null)
  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    row: RowData,
  ) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setSelectedRow(row)
  }
  const handleMenuClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  // グループを追加
  const addGroupClick = (
    event: MouseEvent<HTMLElement>,
    userGroupId: number,
  ) => {
    handleMenuClose(event)
    props.openAddPlaceGroup(userGroupId)
  }

  // 削除ダイアログ
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const handleMenuDelete = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setOpenDeleteDialog(true)
  }
  const handleCloseDeleteDialog = (event: MouseEvent<HTMLElement>) => {
    setOpenDeleteDialog(false)
    handleMenuClose(event)
  }
  const handleDelete = (
    event: MouseEvent<HTMLElement>,
    selectedRow: RowData | null,
  ) => {
    if (selectedRow?.uuid !== undefined) {
      props.deletePlaceGroup(selectedRow.uuid)
    }
    setOpenDeleteDialog(false)
    handleMenuClose(event)
  }

  // アコーディオン
  const isGroupOpen = (id: number) => !collapsedGroup.includes(id)
  const closeGroup = (userGroupId: number) => {
    setCollapsedGroup([...collapsedGroup, userGroupId])
  }
  const openGroup = (userGroupId: number) => {
    setCollapsedGroup([...collapsedGroup.filter(id => id !== userGroupId)])
  }
  const handleToggle = (
    event: React.MouseEvent<HTMLElement>,
    node: FlatNode,
  ) => {
    event.preventDefault()
    event.stopPropagation()
    if (isGroupOpen(node.id)) {
      closeGroup(node.id)
    } else {
      openGroup(node.id)
    }
  }

  if (props.isLoading) {
    return <Loading />
  }

  return (
    <>
      <Table sx={{ bgcolor: 'white', width: '100%' }} stickyHeader>
        <TableHead sx={{ backgroundColor: 'white' }}>
          <TableRow>
            <TableCell
              size={'small'}
              sx={{
                whiteSpace: 'normal',
                backgroundColor: 'white',
              }}
            >
              現場グループ名
            </TableCell>
            <TableCell
              size={'small'}
              sx={{
                whiteSpace: 'normal',
                backgroundColor: 'white',
              }}
            ></TableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={{ backgroundColor: 'white' }}>
          {props.flatNodes
            .filter(node => !node.path.find(p => collapsedGroup.includes(p)))
            .map((node: FlatNode) => {
              const name =
                node.type === PlaceNodeTypeEnum.Place
                  ? node.place?.name
                  : node.placeGroup?.name
              const offsetX = node.path.length * 42
              const isGroup = node.type === PlaceNodeTypeEnum.PlaceGroup
              const isOpen = !collapsedGroup.includes(node.id)
              const key = `${node.uuid ?? node.id ?? 'no-uuid'}-${node.path.join('-')}`
              if (isGroup) {
                // 現場グループの行
                return (
                  <TableRow
                    onClick={() =>
                      props.openEditPlaceGroup(
                        node,
                        node.path.length
                          ? node.path[node.path.length - 1]
                          : undefined,
                      )
                    }
                    key={key}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: semanticColors.colorBgTextHover,
                      },
                    }}
                  >
                    <TableCell
                      size={'small'}
                      sx={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        pl: `${offsetX}px`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        display="flex"
                        alignItems="center"
                        sx={{ gap: 1 }}
                      >
                        <IconButton onClick={e => handleToggle(e, node)}>
                          {isOpen ? <ExpandMore /> : <ChevronRight />}
                        </IconButton>
                        <PlaceGroupSVG color={colors.gray[7]} />
                        <span
                          style={{
                            textAlign: 'left',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {name}
                        </span>
                      </Typography>
                    </TableCell>
                    <TableCell
                      size={'small'}
                      sx={{ minWidth: '96px' }}
                      align="right"
                    >
                      <IconButton
                        onClick={e => {
                          handleMenuClick(e, {
                            id: node.id,
                            uuid: node.uuid,
                            name: name,
                          })
                        }}
                      >
                        <MoreHoriz />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              } else {
                // 現場の行
                return (
                  <TableRow key={key}>
                    <TableCell
                      size={'small'}
                      sx={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        pl: `${10 + offsetX}px`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        py={1}
                        mr={1}
                      >
                        <LocationOn
                          fontSize="small"
                          sx={{ color: colors.gray[7] }}
                        />
                        <span
                          style={{
                            textAlign: 'left',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            marginLeft: '8px',
                          }}
                        >
                          {name}
                        </span>
                      </Typography>
                    </TableCell>
                    <TableCell size={'small'}></TableCell>
                  </TableRow>
                )
              }
            })}
        </TableBody>
      </Table>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={e => {
            addGroupClick(e, selectedRow?.id ?? 0)
          }}
          sx={{
            gap: 1,
          }}
        >
          <Add />
          グループを追加
        </MenuItem>
        <MenuItem
          onClick={handleMenuDelete}
          sx={{
            color: semanticColors.colorErrorText,
            '&:hover': {
              backgroundColor: semanticColors.colorErrorBgHover,
              color: semanticColors.colorErrorText,
            },
            gap: 1,
          }}
        >
          <DeleteOutlined />
          削除
        </MenuItem>
      </Menu>
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>{`「${selectedRow?.name}」の削除を実行しますがよろしいですか？`}</DialogTitle>
        <DialogActions>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleCloseDeleteDialog}
          >
            キャンセル
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={e => handleDelete(e, selectedRow)}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PlaceGroupList
